import { useEffect, useState, useCallback } from "react";
import api, { API } from "@/lib/apiClient";
import { waLink } from "@/lib/modules";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Cake, Gift, PartyPopper, Phone } from "lucide-react";
import { toast } from "sonner";

const MONTHS = ["", "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
const ROLE_LABEL = { coordenador: "Coordenador", lider: "Líder", membro: "Membro", visitante: "Visitante" };

export default function Birthdays() {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [list, setList] = useState([]);
  const [msg, setMsg] = useState("");

  const load = useCallback(() => {
    api.get("/birthdays", { params: { month } }).then((r) => setList(r.data)).catch(() => {});
  }, [month]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/settings").then((r) => setMsg(r.data.birthday_message || "")).catch(() => {}); }, []);

  const buildMsg = (name) => (msg || "🎉 Feliz aniversário, {nome}!").replace(/\{nome\}/g, name.split(" ")[0]);

  const greet = (p) => {
    const link = waLink(p.phone, buildMsg(p.name));
    if (!link) return toast.error("Sem telefone cadastrado para este aniversariante.");
    window.open(link, "_blank");
  };

  const todays = list.filter((p) => p.is_today);

  const greetAll = () => {
    const targets = todays.filter((p) => p.phone);
    if (targets.length === 0) return toast.error("Ninguém com telefone faz aniversário hoje.");
    targets.forEach((p, i) => {
      const link = waLink(p.phone, buildMsg(p.name));
      setTimeout(() => window.open(link, "_blank"), i * 400);
    });
    toast.success(`Abrindo ${targets.length} felicitação(ões) no WhatsApp…`);
  };

  return (
    <div className="space-y-8 animate-fade-up" data-testid="birthdays-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Aniversariantes</h1>
          <p className="text-muted-foreground mt-1">Celebre e envie felicitações aos irmãos dos PGDs.</p>
        </div>
        <div>
          <Label className="text-xs">Mês</Label>
          <select
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            data-testid="birthday-month-select"
            className="mt-1.5 flex h-10 w-48 rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            {MONTHS.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
          </select>
        </div>
      </div>

      {todays.length > 0 && (
        <Card className="p-6 bg-gradient-to-br from-accent/15 to-primary/10 border-accent/30" data-testid="birthdays-today">
          <h3 className="font-display text-xl font-semibold flex items-center gap-2 mb-4">
            <PartyPopper className="h-6 w-6 text-accent" /> Aniversariantes de hoje!
          </h3>
          <Button size="sm" className="gap-2 mb-4" onClick={greetAll} data-testid="greet-all-today-button">
            <Gift className="h-4 w-4" /> Felicitar todos no WhatsApp
          </Button>
          <div className="flex flex-wrap gap-3">
            {todays.map((p) => (
              <div key={p.id} className="flex items-center gap-3 rounded-xl border border-accent/30 bg-card px-4 py-3">
                <Cake className="h-5 w-5 text-accent" />
                <div>
                  <div className="font-semibold">{p.name}</div>
                  <div className="text-xs text-muted-foreground">{p.age} anos</div>
                </div>
                <Button size="sm" className="gap-1.5 ml-2" onClick={() => greet(p)} data-testid={`greet-today-${p.id}`}>
                  <Gift className="h-4 w-4" /> Felicitar
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card className="p-6">
        <h3 className="font-display text-lg font-semibold mb-5">Aniversariantes de {MONTHS[month]}</h3>
        {list.length === 0 ? (
          <p className="text-muted-foreground text-sm py-6 text-center">Nenhum aniversariante neste mês.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="birthdays-list">
            {list.map((p) => (
              <div key={p.id} className="rounded-xl border border-border p-4 flex flex-col gap-3" data-testid="birthday-card">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    {p.avatar_path ? (
                      <img src={`${API}/user-photo/${p.avatar_path}`} alt="" className="h-10 w-10 rounded-full object-cover" />
                    ) : (
                      <div className="h-10 w-10 rounded-full bg-gradient-to-br from-accent to-primary flex items-center justify-center text-sm font-bold text-white">{p.name.charAt(0)}</div>
                    )}
                    <div>
                      <div className="font-semibold">{p.name}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">{ROLE_LABEL[p.role] || p.role} · {p.age} anos</div>
                    </div>
                  </div>
                  <Badge className="bg-accent/15 text-accent border-none shrink-0">
                    {String(p.day).padStart(2, "0")}/{String(p.month).padStart(2, "0")}
                  </Badge>
                </div>
                {p.phone && <div className="text-xs text-muted-foreground flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> {p.phone}</div>}
                <Button size="sm" variant="outline" className="gap-2 mt-auto" onClick={() => greet(p)} data-testid={`greet-${p.id}`}>
                  <Gift className="h-4 w-4" /> Enviar felicitação
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
